/**
 * DataReliabilityTab — Performance → Data tab
 *
 * Renders three zones:
 *   Zone 1: Composite score banner (timeliness + completeness)
 *   Zone 2: Completeness matrix (per sub-KPI reporting status table)
 *           Grouped by: Schedule → Finance → Mission (matching KPI Grid order)
 *           Each group shows its KPI Configuration weight
 *   Zone 3: Reliability history sparkline (placeholder for Phase 2)
 *
 * Due dates are computed via buildReportingDatesFromSchedule() — the shared
 * module used by Schedule/Finance thermometers — ensuring a single source of
 * truth for reporting period boundaries.
 *
 * Uses CSS custom-property tokens and logical properties for theme + RTL.
 */
import React, { useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./DataReliabilityTab.module.css";
import type { MissionSubKpiConfig } from "../../kpi-engine/mission";
import type { InstanceScore } from "../../kpi-engine/instanceScoring";
import { getMissionInstancesFromStorage } from "../../kpi-engine/missionDataAdapter";
import {
  scheduleSeriesStore,
  budgetSeriesStore,
  scheduleMetaStore,
  budgetMetaStore,
  storeKey,
} from "../../Data/store";
import { getProjectOverride } from "../../Data/ProjectConfig/projectConfigOverrides";
import { getProjectKpiConfigOverride } from "../../Data/ProjectConfig/projectKpiConfigOverrides";
import type { SubKpiInstance } from "../../Data/ProjectConfig/projectKpiConfigOverrides";
import { kpiConfig } from "../../Data/KpiEngine/kpiConfig";
// DEV-822 spillover: HSE incidents are event-driven, not cadence-based.
// Data Reliability measures whether scheduled reports are submitted on
// time — that concept doesn't apply to HSE (silence IS the report).
// Filter HSE-typed sub-KPIs out of reliability scoring entirely.
import { excludeHse } from "../../Data/missionSubkpiFilter";
import {
  buildReportingDatesFromSchedule,
  currentReportingDate,
  nextReportingDate,
  parseIsoToLocalMs,
  type ReportingSchedule,
} from "../../kpi-engine/reportingDates";
// DEV-851: Engine v2 composite types — Now (lagging) and Outlook (leading).
// The tab now consumes these directly from the row instead of running its
// own internal scoring blend. Outlook is always applicable=false in v1
// (stub inputs in App.tsx); the panel renders a "Pending" state.
import type {
  CompositeResult,
  ComponentResult,
} from "../../kpi-engine/dataReliability";
// Task #65 — fractional coverage classifier (recency + missing combined
// into one user-facing state). HSE/once already filtered upstream by
// buildExpectedInstancesForProject, so no event-driven branch here.
import {
  classifyReliabilityRow,
  type ReliabilityRowState,
} from "../../Data/DataReliability/perInstanceState";

// ── Types ──────────────────────────────────────────────────────

export type DataReliabilityTabProps = {
  /** The selected project item from the KPI grid row (carries all computed fields). */
  selectedItem: any;
  /** Resolved KPI config for the project. */
  kpiConfig: any;
  /** Debug mode toggle — shows extra diagnostics when true. */
  debugMode?: boolean;
};

/**
 * Status of a single metric row.
 *
 * Legacy values (from the per-period classifyByGap path):
 *   on-time | late | missing | not-due | reported
 *
 * Task #65 v2 values (combined recency + coverage taxonomy — see
 * ReliabilityRowState in src/Data/DataReliability/perInstanceState.ts):
 *   current | backlogged | late-v2 | falling-behind | never-reported | not-yet-due-v2
 *
 * Per Skip's locked mockup (2026-05-11):
 *   - HSE rows are NOT in the matrix at all (event-driven, not cadenced;
 *     enforced upstream by excludeHse in buildExpectedInstancesForProject).
 *   - One-time milestones are NOT in the matrix (filtered upstream when
 *     cadence='once' in the same selector).
 *   - So there is no "na" state and no event-driven row treatment here.
 */
type MetricRowStatus =
  | "on-time"
  | "late"
  | "missing"
  | "not-due"
  | "reported"
  | "current"
  | "backlogged"
  | "late-v2"
  | "falling-behind"
  | "never-reported"
  | "not-yet-due-v2";

/** KPI domain group key matching KPI Grid order. */
type DomainGroup = "schedule" | "finance" | "mission";

type MetricRow = {
  key: string;
  label: string;
  domain: DomainGroup;
  cadence: string;
  lastReported: string | null;
  dueBy: string | null;
  status: MetricRowStatus;
  lateDays: number | null;
  colorValue: number;   // 0=grey, 1=red, 2=yellow, 3=green
  trendValue: -1 | 0 | 1;

  // ── Task #65 — fractional coverage fields ─────────────────────
  // Populated when the matching ExpectedInstanceInput carries
  // expected_periods + missing_periods (the post-DEV-851 production
  // path). When present, the renderer uses the new Status pill +
  // shows the Missing column. When absent, the row falls back to
  // the legacy classifyByGap status.

  /** Periods with zero submissions. null = unknown (legacy row, no coverage data). */
  missing: number | null;

  /** Total cadence periods elapsed since the instance's cadence start. */
  expected: number | null;

  /** v2 state from classifyReliabilityRow; drives the new pill rendering. */
  reliabilityState: import("../../Data/DataReliability/perInstanceState").ReliabilityRowState | null;

  // ── Task #75 — submission timeliness (row level) ──────────────
  // Average submission lag in days across this instance's actuals.
  // mean(max(0, days(created_at - reporting_date))).
  // null when no submissions exist or the lag data isn't supplied.
  // Drives the "Time" column on the matrix.

  /** Average submission lag in days. null = no submissions / not applicable. */
  avgLagDays: number | null;

  /** permissible_days_late from the instance — used to color the Time cell. */
  graceDays: number | null;
};

type GroupSummary = {
  domain: DomainGroup;
  labelKey: string;
  weight: number;
  rows: MetricRow[];
  reportedCount: number;
  totalCount: number;
};

// ── Helpers ────────────────────────────────────────────────────

function formatDateDdMmm(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${String(d).padStart(2, "0")}-${months[Math.min(Math.max(m - 1, 0), 11)]}-${y}`;
}

function trendSymbol(t: -1 | 0 | 1): string {
  if (t === 1) return "▲";
  if (t === -1) return "▼";
  return "●";
}

/** Map internal cadence key to a display-friendly label via i18n. */
const CADENCE_LABEL_KEYS: Record<string, string> = {
  daily: "dataReliability.cadenceDaily",
  weekly: "dataReliability.cadenceWeekly",
  monthly: "dataReliability.cadenceMonthly",
  quarterly: "dataReliability.cadenceQuarterly",
  once: "dataReliability.cadenceOnce",
};

function colorForScore(score: number | null): number {
  if (score === null || !Number.isFinite(score)) return 0;
  if (score >= 80) return 3;
  if (score >= 60) return 2;
  return 1;
}

/** Days between two ISO dates (positive = b is after a). */
function daysBetweenIso(a: string, b: string): number {
  const ma = parseIsoToLocalMs(a);
  const mb = parseIsoToLocalMs(b);
  if (!Number.isFinite(ma) || !Number.isFinite(mb)) return 0;
  return Math.round((mb - ma) / 86_400_000);
}

/** Today as YYYY-MM-DD in local timezone. */
function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Compute the current reporting period's due date using the shared
 * buildReportingDatesFromSchedule() module.
 *
 * Returns { currentDue, nextDue } where currentDue is the most recent
 * reporting date on or before today, and nextDue is the next one after today.
 */
function computeDueDates(
  startDate: string,
  endDate: string,
  schedule: ReportingSchedule | undefined,
  today: string,
): { currentDue: string | null; nextDue: string | null } {
  if (!startDate || !endDate || !schedule) return { currentDue: null, nextDue: null };
  const dates = buildReportingDatesFromSchedule(startDate, endDate, schedule);
  if (dates.length === 0) return { currentDue: null, nextDue: null };
  return {
    currentDue: currentReportingDate(dates, today),
    nextDue: nextReportingDate(dates, today),
  };
}

/**
 * DEV-851: Cadence-based period-boundary fallback for Due By.
 *
 * Used when computeDueDates returns null (typical when the per-instance
 * ReportingSchedule lacks weekday/monthly_mode info — the wizard saves
 * `cadence` but not the full CadenceValue). Mirrors the engine's
 * periodBoundsFor logic in completeness.ts so the matrix's "current due"
 * matches the engine's period boundaries for Completeness scoring.
 *
 *   - daily:     currentDue = today, nextDue = today + 1 day
 *   - weekly:    currentDue = most recent Sunday on or before today,
 *                nextDue    = next Sunday after today
 *   - monthly:   currentDue = last day of previous month,
 *                nextDue    = last day of current month
 *   - quarterly: currentDue = last day of previous quarter,
 *                nextDue    = last day of current quarter
 *
 * Sunday-ending weekly aligns with the engine; if the user's actual
 * cadence picks a different day (e.g., Friday in the wizard screenshot),
 * the period boundary still falls within ±7 days — close enough for the
 * status decision and visual due-date display. Proper fix lives in a
 * follow-up to extend SubKpiInstance to carry the full CadenceValue.
 */
function fallbackPeriodBounds(
  cadence: string,
  today: string,
): { currentDue: string; nextDue: string } {
  const d = new Date(today + "T00:00:00");
  if (!Number.isFinite(d.getTime())) {
    return { currentDue: today, nextDue: today };
  }

  const fmt = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  if (cadence === "daily") {
    const next = new Date(d);
    next.setDate(d.getDate() + 1);
    return { currentDue: fmt(d), nextDue: fmt(next) };
  }

  if (cadence === "weekly") {
    // Period end = most recent Sunday on or before today.
    const day = d.getDay(); // 0 = Sunday
    const cur = new Date(d);
    cur.setDate(d.getDate() - day);
    const next = new Date(cur);
    next.setDate(cur.getDate() + 7);
    return { currentDue: fmt(cur), nextDue: fmt(next) };
  }

  if (cadence === "quarterly") {
    const month = d.getMonth();
    const quarterEndMonth = month >= 9 ? 8 : month >= 6 ? 5 : month >= 3 ? 2 : 11;
    const yearForEnd = month >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    const cur = new Date(yearForEnd, quarterEndMonth + 1, 0);
    const next = new Date(yearForEnd, quarterEndMonth + 4, 0);
    return { currentDue: fmt(cur), nextDue: fmt(next) };
  }

  // Default to monthly
  const cur = new Date(d.getFullYear(), d.getMonth(), 0); // last day of previous month
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 0); // last day of current month
  return { currentDue: fmt(cur), nextDue: fmt(next) };
}

/** Apply the cadence-based fallback when computeDueDates failed to produce dates. */
function withFallbackBounds(
  primary: { currentDue: string | null; nextDue: string | null },
  cadence: string,
  today: string,
): { currentDue: string | null; nextDue: string | null } {
  if (primary.currentDue) return primary;
  const fb = fallbackPeriodBounds(cadence, today);
  return { currentDue: fb.currentDue, nextDue: fb.nextDue };
}

/** Map cadence string → expected days between consecutive reports. */
function cadenceToDays(cadence: string): number {
  switch (cadence) {
    case "daily":     return 1;
    case "weekly":    return 7;
    case "quarterly": return 91;
    case "once":      return 0; // one-shot — gap-based check doesn't apply
    case "monthly":
    default:          return 30;
  }
}

/**
 * DEV-851: Gap-based lateness classifier.
 *
 * "If the user reports weekly and the last entry was 29 days ago, that's
 * 4 missed weeks — late." The existing computeStatus only asks "are we
 * past current-period-end + grace today?" which misses skipped periods
 * entirely (returns on-time as long as today is within grace of the most
 * recent boundary, regardless of how stale the actual data is).
 *
 * Worst-of-three semantics:
 *   1. Gap lateness  — (today - lastReported) - cadence_days - grace
 *   2. Submission lateness — (lastCreatedAt - lastReported) - grace
 *   3. (computeStatus's period-vs-grace already considered upstream)
 *
 * The largest positive number wins. If all are ≤ 0, the row is on-time.
 */
function classifyByGap(args: {
  lastReported: string | null;
  lastCreatedAt: string | null;
  cadence: string;
  today: string;
  grace: number;
  /** Existing per-period status (from computeStatus) used as floor. */
  baseStatus: MetricRowStatus;
  baseLateDays: number | null;
}): { status: MetricRowStatus; lateDays: number | null } {
  // Preserve missing / not-due / reported semantics from upstream check.
  if (args.baseStatus === "missing" || args.baseStatus === "not-due" || args.baseStatus === "reported") {
    return { status: args.baseStatus, lateDays: args.baseLateDays };
  }
  if (!args.lastReported) {
    return { status: args.baseStatus, lateDays: args.baseLateDays };
  }

  const cadenceDays = cadenceToDays(args.cadence);
  let worstLate = args.baseStatus === "late" && args.baseLateDays != null ? args.baseLateDays : 0;

  // Gap lateness — only meaningful for recurring cadences (cadenceDays > 0).
  if (cadenceDays > 0) {
    const daysSinceLast = daysBetweenIso(args.lastReported, args.today);
    const gapLate = daysSinceLast - cadenceDays - args.grace;
    if (gapLate > worstLate) worstLate = gapLate;
  }

  // Submission lateness — when the entry was submitted late vs its
  // reporting_date plus grace.
  if (args.lastCreatedAt) {
    const submissionLag = daysBetweenIso(args.lastReported, args.lastCreatedAt) - args.grace;
    if (submissionLag > worstLate) worstLate = submissionLag;
  }

  if (worstLate > 0) return { status: "late", lateDays: worstLate };
  return { status: "on-time", lateDays: null };
}

/**
 * Determine metric row status from last-reported date vs due date.
 *
 * Logic:
 *   - If no due date can be computed → fall back to simple has-data check
 *   - If no lastReported → "missing" (due date exists, no data submitted)
 *   - If lastReported >= currentDue → "on-time"
 *   - If lastReported < currentDue → "late" (with days count)
 *   - If today < first due date → "not-due"
 */
function computeStatus(
  lastReported: string | null,
  currentDue: string | null,
  nextDue: string | null,
  today: string,
  graceDays: number,
): { status: MetricRowStatus; lateDays: number | null; dueBy: string | null } {
  // No due date computable — simple fallback
  if (!currentDue) {
    if (!lastReported) return { status: "not-due", lateDays: null, dueBy: null };
    return { status: "on-time", lateDays: null, dueBy: null };
  }

  // The "due by" for display is the next upcoming reporting date (the deadline)
  const displayDue = nextDue ?? currentDue;

  if (!lastReported) {
    // Check if today is before the first due date (with grace)
    const daysFromDue = daysBetweenIso(currentDue, today);
    if (daysFromDue < 0) {
      return { status: "not-due", lateDays: null, dueBy: displayDue };
    }
    if (daysFromDue <= graceDays) {
      return { status: "missing", lateDays: null, dueBy: displayDue };
    }
    return { status: "missing", lateDays: daysFromDue - graceDays, dueBy: displayDue };
  }

  // Has data — check if it covers the current period
  if (lastReported >= currentDue) {
    return { status: "on-time", lateDays: null, dueBy: displayDue };
  }

  // Data exists but doesn't cover the current period
  const daysLate = daysBetweenIso(currentDue, today) - graceDays;
  if (daysLate <= 0) {
    // Within grace period — still on time
    return { status: "on-time", lateDays: null, dueBy: displayDue };
  }
  return { status: "late", lateDays: daysLate, dueBy: displayDue };
}

// ── Component ──────────────────────────────────────────────────

export default function DataReliabilityTab({
  selectedItem,
  kpiConfig: kpiConfigProp,
  debugMode = false,
}: DataReliabilityTabProps) {
  const { t } = useAppTranslation("kpi");

  // ── Pull data from selectedItem (already computed by engine + App.tsx) ──
  //
  // DEV-851: Headline composites are read directly from the row. Now drives
  // the upper panel (lagging signal — Completeness + Timeliness from engine
  // v2). Outlook drives the lower panel (leading signal — always N/A in v1
  // until DEV-851e wires the data feeds).
  //
  // DEV-851 (PR2): legacy `data_reliability_subkpis` row field retired.
  // The mission matrix row builder previously used it as a status fallback;
  // that path now degrades cleanly to "data exists → on-time, no data →
  // missing" via the `cadence === "once"` and `missionCurrentDue` branches,
  // which are sufficient for all currently configured cadences.
  const now: CompositeResult | undefined = (selectedItem as any)?.data_reliability_now;
  const outlook: CompositeResult | undefined = (selectedItem as any)?.data_reliability_outlook;
  const isCompletedProject: boolean = !!(selectedItem as any)?._isCompletedProject;
  const asOfDate: string = (selectedItem as any)?.data_reliability_as_of_date ?? (selectedItem as any)?.kpi_as_of_date ?? "";
  const projectGuid: string = (selectedItem as any)?.project_guid ?? "";

  // ── Project dates and cadence ──
  const today = useMemo(() => todayIso(), []);
  const projConfig = useMemo(() => {
    if (!projectGuid) return undefined;
    return getProjectOverride(projectGuid as any);
  }, [projectGuid]);

  const projectStartDate: string = String(
    (projConfig as any)?.project_start_date
    ?? (selectedItem as any)?.project_start_date
    ?? (selectedItem as any)?.start_date
    ?? (selectedItem as any)?.startDate
    ?? ""
  ).trim();

  const projectEndDate: string = String(
    (projConfig as any)?.project_end_date
    ?? (selectedItem as any)?.project_end_date
    ?? (selectedItem as any)?.finish_date
    ?? (selectedItem as any)?.end_date
    ?? (selectedItem as any)?.finishDate
    ?? ""
  ).trim();

  // Project-level reporting schedule (for Mission sub-KPIs)
  const projectReportingSchedule: ReportingSchedule | undefined = useMemo(() => {
    const sched = (projConfig as any)?.reporting_schedule;
    if (sched?.cadence) return sched as ReportingSchedule;
    // Fallback: build minimal schedule from legacy reporting_cadence
    const legacy = String((projConfig as any)?.reporting_cadence ?? "monthly");
    const cadence = (["daily","weekly","monthly","quarterly"].includes(legacy) ? legacy : "monthly") as ReportingSchedule["cadence"];
    return { cadence, interval: 1 };
  }, [projConfig]);

  const defaultGraceDays = (kpiConfigProp as any)?.data_reliability?.default_permissible_days_late ?? 2;

  // DEV-851: Completed-project state is now sourced from the row's
  // `_isCompletedProject` flag (computed in App.tsx via the canonical
  // `isProjectCompleted` util — single source of truth across surfaces).
  // The local derivation here was deleted along with the score=100 hack;
  // the engine's lifecycle gate handles the composite N/A on its end and
  // the UI applies the blue Complete-state banner via the flag below.

  // ── Category weights from per-project KPI config → global fallback ──
  const categoryWeights = useMemo(() => {
    const defaults = (kpiConfig as any)?.summary?.weights ?? { schedule: 25, finance: 25, mission: 25, data_reliability: 25 };
    if (!projectGuid) return defaults;
    const ov = getProjectKpiConfigOverride(projectGuid as any);
    if (ov?.category_weights) {
      return { ...defaults, ...ov.category_weights };
    }
    return defaults;
  }, [projectGuid]);

  // ── Mission sub-KPI config (source of metric definitions) ──
  // Priority: per-project instances (from KPI config store) → global seed config.
  // The per-project instances carry the actual configured cadence and deadline_date.
  const missionInstancesFromStore = useMemo(
    () => (projectGuid ? getMissionInstancesFromStorage(projectGuid) : []),
    [projectGuid],
  );
  const missionInstanceLookup = useMemo(() => {
    const map = new Map<string, (typeof missionInstancesFromStore)[0]>();
    for (const inst of missionInstancesFromStore) {
      // Key by outcome_id (primary), then instance_guid, then display_name — same logic as App.tsx
      const key = inst.outcome_id || inst.instance_guid || inst.display_name || "";
      if (key) map.set(key.toUpperCase(), inst);
      // Also index by display_name for label-based matching
      if (inst.display_name) map.set(inst.display_name.toUpperCase(), inst);
    }
    return map;
  }, [missionInstancesFromStore]);

  const missionConfig: Record<string, MissionSubKpiConfig> =
    (kpiConfigProp as any)?.mission?.subkpis ?? {};
  // Drop HSE-typed entries — see import comment.
  const missionSubkpis: any[] = excludeHse(
    (selectedItem as any)?.mission_subkpis ?? [],
  );

  // ── Schedule & Finance instance scores (already on selectedItem) ──
  // The scoring engine may emit multiple scored items per physical instance
  // (e.g. progress_spi splits one instance into Progress + SPI views).
  // For reliability, we need one row per unique instance_guid — deduplicate
  // by keeping the first entry per guid (which is always the Progress variant).
  // DEV-946: extract async-derived arrays into intermediate memos so
  // useMemo deps can be statically checked AND the empty-array fallback
  // produces a stable reference. Previously the deps array contained
  // `(selectedItem as any)?.schedule_instance_scores` directly, which the
  // React-Hooks linter cannot statically verify (and which, structurally,
  // hid that `selectedItem` itself was a missing dep). The first revision
  // of this fix used plain const, but `?? []` creates a fresh array every
  // render when the left side is null/undefined — so the downstream memos
  // re-ran every render anyway. useMemo restores reference stability.
  const rawScheduleInstanceScores: InstanceScore[] = useMemo(
    () => (selectedItem as any)?.schedule_instance_scores ?? [],
    [selectedItem],
  );
  const rawFinanceInstanceScores: InstanceScore[] = useMemo(
    () => (selectedItem as any)?.finance_instance_scores ?? [],
    [selectedItem],
  );

  const scheduleInstanceScores: InstanceScore[] = useMemo(() => {
    const seen = new Set<string>();
    return rawScheduleInstanceScores.filter((inst) => {
      const guid = String(inst.instance_guid ?? "").toUpperCase();
      if (!guid || seen.has(guid)) return false;
      seen.add(guid);
      return true;
    });
  }, [rawScheduleInstanceScores]);

  const financeInstanceScores: InstanceScore[] = useMemo(() => {
    const seen = new Set<string>();
    return rawFinanceInstanceScores.filter((inst) => {
      const guid = String(inst.instance_guid ?? "").toUpperCase();
      if (!guid || seen.has(guid)) return false;
      seen.add(guid);
      return true;
    });
  }, [rawFinanceInstanceScores]);

  // DEV-851 (matrix-vs-engine alignment fix):
  // The user-configured cadence + permissible_days_late live on the
  // SubKpiInstance (kpi config store), NOT on the curve metadata store.
  // The previous code read scheduleMetaStore.reporting_schedule, which is
  // typically empty because the wizard saves cadence to a different store.
  // Result: matrix defaulted to monthly cadence and produced null `currentDue`
  // even for projects with weekly KPIs configured. We now look up the
  // SubKpiInstance directly — same pattern Mission rows already use.
  const scheduleInstanceConfigs = useMemo(() => {
    const map = new Map<string, SubKpiInstance>();
    if (!projectGuid) return map;
    const ov = getProjectKpiConfigOverride(projectGuid as any);
    for (const inst of (ov?.category_instances?.schedule ?? [])) {
      const guid = String(inst.instance_guid ?? "").toUpperCase();
      if (guid) map.set(guid, inst);
    }
    return map;
  }, [projectGuid]);

  const financeInstanceConfigs = useMemo(() => {
    const map = new Map<string, SubKpiInstance>();
    if (!projectGuid) return map;
    const ov = getProjectKpiConfigOverride(projectGuid as any);
    for (const inst of (ov?.category_instances?.finance ?? [])) {
      const guid = String(inst.instance_guid ?? "").toUpperCase();
      if (guid) map.set(guid, inst);
    }
    return map;
  }, [projectGuid]);

  // ── Build grouped completeness matrix rows ──
  const groups: GroupSummary[] = useMemo(() => {
    // ─────── Schedule rows ───────
    const scheduleRows: MetricRow[] = [];
    for (const inst of scheduleInstanceScores) {
      const iGuid = String(inst.instance_guid ?? "");
      const sk = storeKey(projectGuid, iGuid || undefined);
      const series = scheduleSeriesStore.get(sk) ?? [];
      const meta = scheduleMetaStore.get(sk);

      // Last reported = latest point with non-null actual_complete
      const withActual = series.filter((p) => p.actual_complete !== null);
      const lastPoint = withActual.length > 0 ? withActual[withActual.length - 1] : null;
      const lastDate = lastPoint?.reporting_date ?? null;
      // DEV-851: per-row submission timestamp. Carried through scheduleSeriesPoint
      // → seed-shape (with `created_at` preserved) → here. Used for the per-entry
      // lateness check below — same rule the engine's Timeliness component uses.
      const lastCreatedAt = String((lastPoint as any)?.created_at ?? "");

      // DEV-851: Prefer per-instance config (where the wizard saves cadence
      // and grace). Fall back to scheduleMetaStore for legacy curves that
      // didn't go through the new wizard flow.
      const instanceConfig = scheduleInstanceConfigs.get(iGuid.toUpperCase());
      const cadence = String(
        instanceConfig?.cadence
        ?? meta?.reporting_schedule?.cadence
        ?? "weekly"
      );
      const grace = Number.isFinite(instanceConfig?.permissible_days_late)
        ? (instanceConfig!.permissible_days_late as number)
        : defaultGraceDays;

      // Compute due dates from this instance's own curve schedule
      const curveStart = String(meta?.baseline_start_date_override ?? meta?.baseline_start_date ?? projectStartDate).trim();
      const curveEnd = String(meta?.baseline_end_date_override ?? meta?.baseline_end_date ?? projectEndDate).trim();
      const instanceSchedule: ReportingSchedule | undefined = meta?.reporting_schedule
        ? (meta.reporting_schedule as ReportingSchedule)
        : { cadence: cadence as ReportingSchedule["cadence"], interval: 1 };

      // DEV-851: cadence-based fallback when the per-instance schedule
      // can't produce a concrete current-due via buildReportingDatesFromSchedule
      // (typical when SubKpiInstance.cadence is configured but the wizard's
      // weekday selection isn't persisted yet).
      const primaryDates = computeDueDates(curveStart, curveEnd, instanceSchedule, today);
      const { currentDue, nextDue } = withFallbackBounds(primaryDates, cadence, today);
      const periodResult = computeStatus(lastDate, currentDue, nextDue, today, grace);
      const dueBy = periodResult.dueBy;

      // DEV-851: classify with worst-of (period-late, gap-late, submission-late).
      // Gap-late catches "user skipped multiple weekly periods" — the period-vs-grace
      // rule alone misses this because it only asks "are we past current period
      // boundary + grace today?" not "did the user actually report within the
      // expected cadence?"
      const { status, lateDays } = classifyByGap({
        lastReported: lastDate,
        lastCreatedAt: lastCreatedAt || null,
        cadence,
        today,
        grace,
        baseStatus: periodResult.status,
        baseLateDays: periodResult.lateDays,
      });

      const colorValue = status === "on-time" ? 3
        : status === "late" ? 2
        : status === "missing" ? 1
        : 0;

      // Strip scoring-engine suffixes like " (Progress)" / " (SPI)" from display name
      const cleanName = (inst.display_name || "")
        .replace(/\s*\(Progress\)\s*$/i, "")
        .replace(/\s*\(SPI\)\s*$/i, "")
        .trim();

      scheduleRows.push({
        key: `sched_${iGuid}`,
        label: cleanName || t("dataReliability.scheduleDefault") || "Schedule",
        domain: "schedule",
        cadence,
        lastReported: lastDate,
        dueBy,
        status,
        lateDays,
        colorValue,
        trendValue: inst.trend ?? 0,
        // Task #65 fractional fields — populated by the enrichment pass below.
        missing: null,
        expected: null,
        reliabilityState: null,
        // Task #75 — Time column fields. Populated by enrichment.
        avgLagDays: null,
        graceDays: null,
      });
    }

    // ─────── Finance rows ───────
    const financeRows: MetricRow[] = [];
    for (const inst of financeInstanceScores) {
      const iGuid = String(inst.instance_guid ?? "");
      const sk = storeKey(projectGuid, iGuid || undefined);
      const series = budgetSeriesStore.get(sk) ?? [];
      const meta = budgetMetaStore.get(sk);

      // Last reported = latest point with non-null actual_spend
      const withActual = series.filter((p) => p.actual_spend !== null);
      const lastPoint = withActual.length > 0 ? withActual[withActual.length - 1] : null;
      const lastDate = lastPoint?.reporting_date ?? null;
      const lastCreatedAt = String((lastPoint as any)?.created_at ?? "");

      // DEV-851: prefer per-instance wizard config; see Schedule branch above.
      const instanceConfig = financeInstanceConfigs.get(iGuid.toUpperCase());
      const cadence = String(
        instanceConfig?.cadence
        ?? meta?.reporting_schedule?.cadence
        ?? "weekly"
      );
      const grace = Number.isFinite(instanceConfig?.permissible_days_late)
        ? (instanceConfig!.permissible_days_late as number)
        : defaultGraceDays;

      // Compute due dates from this instance's own curve schedule
      const curveStart = String(meta?.baseline_start_date_override ?? meta?.baseline_start_date ?? projectStartDate).trim();
      const curveEnd = String(meta?.baseline_end_date_override ?? meta?.baseline_end_date ?? projectEndDate).trim();
      const instanceSchedule: ReportingSchedule | undefined = meta?.reporting_schedule
        ? (meta.reporting_schedule as ReportingSchedule)
        : { cadence: cadence as ReportingSchedule["cadence"], interval: 1 };

      // DEV-851: cadence-based fallback for due dates + worst-of lateness.
      // See Schedule branch for full rationale.
      const primaryDates = computeDueDates(curveStart, curveEnd, instanceSchedule, today);
      const { currentDue, nextDue } = withFallbackBounds(primaryDates, cadence, today);
      const periodResult = computeStatus(lastDate, currentDue, nextDue, today, grace);
      const dueBy = periodResult.dueBy;

      const { status, lateDays } = classifyByGap({
        lastReported: lastDate,
        lastCreatedAt: lastCreatedAt || null,
        cadence,
        today,
        grace,
        baseStatus: periodResult.status,
        baseLateDays: periodResult.lateDays,
      });

      const colorValue = status === "on-time" ? 3
        : status === "late" ? 2
        : status === "missing" ? 1
        : 0;

      const cleanFinName = (inst.display_name || "")
        .replace(/\s*\(Progress\)\s*$/i, "")
        .replace(/\s*\(SPI\)\s*$/i, "")
        .trim();

      financeRows.push({
        key: `fin_${iGuid}`,
        label: cleanFinName || t("dataReliability.financeDefault") || "Budget",
        domain: "finance",
        cadence,
        lastReported: lastDate,
        dueBy,
        status,
        lateDays,
        colorValue,
        trendValue: inst.trend ?? 0,
        // Task #65 fractional fields — populated by the enrichment pass below.
        missing: null,
        expected: null,
        reliabilityState: null,
        // Task #75 — Time column fields. Populated by enrichment.
        avgLagDays: null,
        graceDays: null,
      });
    }

    // ─────── Mission rows ───────
    const missionRows: MetricRow[] = [];

    // Project-level reporting schedule (fallback for mission KPIs without per-instance deadline)
    const { currentDue: missionCurrentDue, nextDue: missionNextDue } =
      computeDueDates(projectStartDate, projectEndDate, projectReportingSchedule, today);

    for (const sk of missionSubkpis) {
      const key = String(sk?.subKpiKey ?? "");
      if (!key) continue;

      // ── Resolve per-instance config: store instance → engine result → global seed ──
      const storeInst = missionInstanceLookup.get(key.toUpperCase())
        ?? missionInstanceLookup.get(String(sk?.label ?? "").toUpperCase());

      const cfg = missionConfig[key] ??
        Object.values(missionConfig).find(
          (v: any) => String(v?.label ?? "").toUpperCase() === String(sk?.label ?? "").toUpperCase()
        );

      // Cadence: per-project instance → engine result → global seed → "once" (safest default for mission)
      const cadence = String(
        storeInst?.cadence ?? sk?.cadence ?? cfg?.cadence ?? "once"
      );

      // Deadline: per-project instance → engine result (passed through from config)
      const deadlineDate: string | null = String(
        storeInst?.deadline_date ?? sk?.deadline_date ?? cfg?.deadline_date ?? ""
      ).trim() || null;

      const lastDate: string | null = sk?.latest_date ?? null;
      const grace = Number.isFinite(storeInst?.permissible_days_late)
        ? storeInst!.permissible_days_late!
        : Number.isFinite(cfg?.permissible_days_late)
          ? (cfg as any).permissible_days_late
          : defaultGraceDays;

      // ── Determine due dates based on cadence type ──
      let status: MetricRowStatus;
      let lateDays: number | null = null;
      let dueBy: string | null = null;

      // DEV-851: per-row submission timestamp from missionRawPoints (filtered
      // to this project on the row by App.tsx). Used by classifyByGap below.
      const missionPoints: any[] = (selectedItem as any)?.mission_raw_points ?? [];
      const lastPointForKpi = missionPoints
        .filter((p) => String(p?.subKpiKey || "").toUpperCase() === key.toUpperCase())
        .reduce((acc: any, p: any) => {
          const d = String(p?.reportingDate || "");
          if (!d) return acc;
          if (!acc || d > String(acc?.reportingDate || "")) return p;
          return acc;
        }, null);
      const lastCreatedAt = String(lastPointForKpi?.enteredAt || lastPointForKpi?.createdAt || "");

      if (cadence === "once") {
        // One-time / milestone metrics: due date is the deadline_date, no "next" due
        if (deadlineDate) {
          const result = computeStatus(lastDate, deadlineDate, null, today, grace);
          status = result.status;
          lateDays = result.lateDays;
          dueBy = result.dueBy;
        } else {
          // No deadline configured — if data exists it's on-time, otherwise not-due
          status = lastDate ? "on-time" : "not-due";
        }
      } else {
        // DEV-851: Recurring cadence — apply cadence-based fallback for due
        // dates when project-level schedule can't compute (typical when the
        // project's reporting_schedule lacks weekday info).
        const primary = { currentDue: missionCurrentDue, nextDue: missionNextDue };
        const { currentDue, nextDue } = withFallbackBounds(primary, cadence, today);
        const result = computeStatus(lastDate, currentDue, nextDue, today, grace);
        status = result.status;
        lateDays = result.lateDays;
        dueBy = result.dueBy;

        // Apply gap-based + submission-based worst-of (Schedule branch comment).
        const gapClassified = classifyByGap({
          lastReported: lastDate,
          lastCreatedAt: lastCreatedAt || null,
          cadence,
          today,
          grace,
          baseStatus: status,
          baseLateDays: lateDays,
        });
        status = gapClassified.status;
        lateDays = gapClassified.lateDays;
      }

      const colorValue = status === "on-time" ? 3
        : status === "late" ? 2
        : status === "missing" ? 1
        : 0;

      missionRows.push({
        key: `mission_${key}`,
        label: String(sk?.label ?? key),
        domain: "mission",
        cadence,
        lastReported: lastDate,
        dueBy,
        status,
        lateDays,
        colorValue,
        trendValue: sk?.indicator_trend_value ?? 0,
        // Task #65 fractional fields — populated by the enrichment pass below.
        missing: null,
        expected: null,
        reliabilityState: null,
        // Task #75 — Time column fields. Populated by enrichment.
        avgLagDays: null,
        graceDays: null,
      });
    }

    // ─────── Task #65 — enrich + back-fill from expected_instances ───────
    //
    // App.tsx populates selectedItem.data_reliability_expected_instances with
    // every configured KPI instance (non-HSE, non-once), each carrying the
    // fractional coverage fields (expected_periods, reported_periods,
    // missing_periods) plus the latest_reporting_date.
    //
    // Two passes here:
    //
    //   ENRICH — for each existing row that came out of the scoring path,
    //   find its matching ExpectedInstanceInput by category + key and copy
    //   the coverage fields onto the row. Then call classifyReliabilityRow
    //   to assign the v2 Status. The row's legacy `status`, `lateDays`,
    //   `dueBy` etc. are left untouched so the legacy renderer can still
    //   fall back when reliabilityState is null.
    //
    //   BACK-FILL — for every ExpectedInstanceInput NOT already represented
    //   in the row arrays (configured-but-never-reported instances, the
    //   gap that caused the 57%-vs-7-of-7 discrepancy Skip flagged), push
    //   a synthesized never-reported / not-yet-due row into the right
    //   group. The matrix denominator now matches the engine's.
    //
    //   HSE is excluded upstream by buildExpectedInstancesForProject
    //   (excludeHse). cadence='once' is also excluded upstream. So every
    //   ExpectedInstanceInput here belongs in the matrix.
    const expectedInstancesRaw: any[] =
      (selectedItem as any)?.data_reliability_expected_instances ?? [];

    const findRowForInstance = (
      inst: { category: string; instance_id: string },
      domainRows: { schedule: MetricRow[]; finance: MetricRow[]; mission: MetricRow[] },
    ): MetricRow | undefined => {
      const id = String(inst.instance_id ?? "").toUpperCase();
      if (inst.category === "schedule") {
        return domainRows.schedule.find(
          (r) => r.key === `sched_${id}` || r.key.toUpperCase() === `SCHED_${id}`,
        );
      }
      if (inst.category === "finance") {
        return domainRows.finance.find(
          (r) => r.key === `fin_${id}` || r.key.toUpperCase() === `FIN_${id}`,
        );
      }
      return domainRows.mission.find(
        (r) => r.key === `mission_${id}` || r.key.toUpperCase() === `MISSION_${id}`,
      );
    };

    // Convert v2 Comp. state → severity number (3 green / 2 yellow /
    // 1 red / 0 N/A). Higher = better. The bar color and the right-side
    // pill color both pull from this; the bar additionally combines with
    // lag severity below.
    const v2ColorFromState = (s: ReliabilityRowState): number => {
      switch (s) {
        case "current":
          return 3;
        case "backlogged":
        case "late":
          return 2;
        case "falling-behind":
        case "never-reported":
          return 1;
        case "not-yet-due":
        case "na":
        default:
          return 0;
      }
    };

    // Map avg lag → severity using the same color bands as the Avg. Lag
    // column: ≤ grace = green (3), grace+1..+7 = yellow (2), > grace+7 =
    // red (1). null lag = 0 (N/A, doesn't influence the bar).
    const severityFromLag = (
      lag: number | null | undefined,
      grace: number | null | undefined,
    ): number => {
      if (lag == null || !Number.isFinite(lag)) return 0;
      const g = Number.isFinite(grace) ? (grace as number) : 0;
      if (lag <= g) return 3;
      if (lag <= g + 7) return 2;
      return 1;
    };

    // Combine two severities — keep the WORSE one (lower number wins),
    // treating 0 as "N/A, not measurable" so it never overrides a real
    // signal. If both are 0 → 0. If exactly one is 0 → the other.
    // If both non-zero → the smaller of the two.
    const combineSeverity = (a: number, b: number): number => {
      if (a === 0) return b;
      if (b === 0) return a;
      return Math.min(a, b);
    };

    const v2StatusToMetricStatus = (s: ReliabilityRowState): MetricRowStatus => {
      switch (s) {
        case "current":
          return "current";
        case "backlogged":
          return "backlogged";
        case "late":
          return "late-v2";
        case "falling-behind":
          return "falling-behind";
        case "never-reported":
          return "never-reported";
        case "not-yet-due":
          return "not-yet-due-v2";
        case "na":
        default:
          return "not-yet-due-v2";
      }
    };

    const domainRows = {
      schedule: scheduleRows,
      finance: financeRows,
      mission: missionRows,
    };

    for (const inst of expectedInstancesRaw) {
      const hasCoverage =
        Number.isFinite(inst?.expected_periods) &&
        Number.isFinite(inst?.missing_periods);
      const state = classifyReliabilityRow({
        cadence: String(inst?.cadence ?? "weekly"),
        latestReportingDate: inst?.latest_reporting_date ?? null,
        asOfDate: today,
        graceDays: Number.isFinite(inst?.grace_days)
          ? (inst.grace_days as number)
          : defaultGraceDays,
        expectedPeriods: hasCoverage ? Number(inst.expected_periods) : 0,
        missingPeriods: hasCoverage ? Number(inst.missing_periods) : 0,
      });

      const existing = findRowForInstance(
        inst as { category: string; instance_id: string },
        domainRows,
      );

      if (existing) {
        // Enrich existing row produced by the scoring path.
        existing.missing = hasCoverage ? Number(inst.missing_periods) : null;
        existing.expected = hasCoverage ? Number(inst.expected_periods) : null;
        existing.reliabilityState = state;
        existing.status = v2StatusToMetricStatus(state);
        // Task #75 — Time column inputs.
        existing.avgLagDays = Number.isFinite(inst?.avg_lag_days)
          ? Number(inst.avg_lag_days)
          : null;
        existing.graceDays = Number.isFinite(inst?.grace_days)
          ? Number(inst.grace_days)
          : null;
        // Bar color = worst-of completeness × timeliness. A row that's
        // complete but chronically late should still show non-green on
        // the bar so it can be spotted in a quick scan.
        existing.colorValue = combineSeverity(
          v2ColorFromState(state),
          severityFromLag(existing.avgLagDays, existing.graceDays),
        );
        continue;
      }

      // Back-fill: never-reported or not-yet-due instance the scoring
      // path didn't surface. Synthesize a minimal row.
      const id = String(inst?.instance_id ?? "").toUpperCase();
      const category = String(inst?.category ?? "mission") as DomainGroup;
      const lookupLabel = (selectedItem as any)?.data_reliability_instance_labels?.[id];
      const label =
        (lookupLabel && (lookupLabel.label || lookupLabel))
        || id
        || "(unnamed)";

      const syntheticAvgLag = Number.isFinite(inst?.avg_lag_days)
        ? Number(inst.avg_lag_days)
        : null;
      const syntheticGrace = Number.isFinite(inst?.grace_days)
        ? Number(inst.grace_days)
        : null;
      const synthetic: MetricRow = {
        key: `${category}_${id}_synth`,
        label: String(label),
        domain: category,
        cadence: String(inst?.cadence ?? ""),
        lastReported: inst?.latest_reporting_date ?? null,
        dueBy: null,
        status: v2StatusToMetricStatus(state),
        lateDays: null,
        // Worst-of bar color, same combine rule as the enrichment branch.
        colorValue: combineSeverity(
          v2ColorFromState(state),
          severityFromLag(syntheticAvgLag, syntheticGrace),
        ),
        trendValue: 0,
        missing: hasCoverage ? Number(inst.missing_periods) : null,
        expected: hasCoverage ? Number(inst.expected_periods) : null,
        reliabilityState: state,
        avgLagDays: syntheticAvgLag,
        graceDays: syntheticGrace,
      };

      if (category === "schedule") scheduleRows.push(synthetic);
      else if (category === "finance") financeRows.push(synthetic);
      else missionRows.push(synthetic);
    }

    // ─────── Assemble groups in KPI Grid order ───────
    const result: GroupSummary[] = [];
    const groupDefs: { domain: DomainGroup; labelKey: string; rows: MetricRow[] }[] = [
      { domain: "schedule", labelKey: "dataReliability.groupSchedule", rows: scheduleRows },
      { domain: "finance", labelKey: "dataReliability.groupFinance", rows: financeRows },
      { domain: "mission", labelKey: "dataReliability.groupMission", rows: missionRows },
    ];

    for (const gd of groupDefs) {
      // Task #65: "fully caught up" now means reliabilityState === 'current'
      // when set, else falls back to the legacy "not missing / not-due" rule.
      // The phrasing in the matrix header should read "X of Y current" for v2
      // rows; the legacy reading is preserved for rows that haven't been
      // enriched (defensive — shouldn't happen in production but matters
      // during the rollout window).
      const reported = gd.rows.filter((r) => {
        if (r.reliabilityState) return r.reliabilityState === "current";
        return r.status !== "missing" && r.status !== "not-due";
      }).length;
      // Only include groups that have configured metrics
      if (gd.rows.length > 0) {
        result.push({
          domain: gd.domain,
          labelKey: gd.labelKey,
          weight: categoryWeights[gd.domain] ?? 25,
          rows: gd.rows,
          reportedCount: reported,
          totalCount: gd.rows.length,
        });
      }
    }

    return result;
    // DEV-946: added selectedItem (mission_raw_points read inside),
    // scheduleInstanceConfigs and financeInstanceConfigs (used in row
    // assembly above). All three were silently missing — the memo would
    // not recompute when any of them updated, leaving the data-reliability
    // groups showing stale rows after late-arriving API data.
  }, [
    scheduleInstanceScores, financeInstanceScores, missionSubkpis,
    missionConfig, missionInstanceLookup, projectGuid, categoryWeights,
    projectStartDate, projectEndDate, projectReportingSchedule,
    today, defaultGraceDays, t,
    selectedItem, scheduleInstanceConfigs, financeInstanceConfigs,
  ]);

  // ── Remap statuses for completed/archival projects ──
  // For completed projects, "on-time" is misleading — the data was entered
  // retroactively, so we can't judge timeliness.  Use "reported" instead,
  // which accurately says "data exists" without implying temporal compliance.
  // Also remap "late" → "reported" for the same reason.
  const displayGroups: GroupSummary[] = useMemo(() => {
    if (!isCompletedProject) return groups;
    return groups.map((g) => ({
      ...g,
      rows: g.rows.map((r) => {
        if (r.status === "on-time" || r.status === "late") {
          return { ...r, status: "reported" as MetricRowStatus, lateDays: null, colorValue: 4 };
        }
        return r;
      }),
    }));
  }, [groups, isCompletedProject]);

  // ── Matrix counters (used by empty-state check and the row count line) ──
  // The headline score / breakdown / trend are now sourced from `now` and
  // `outlook` (engine v2). The matrix counters remain locally computed
  // because the matrix structure is per-sub-KPI (richer than the engine's
  // per-category Completeness component).
  const allRows = displayGroups.flatMap((g) => g.rows);
  const reportedCount = allRows.filter((r) => r.status !== "missing" && r.status !== "not-due").length;
  const totalCount = allRows.length;

  // ── Variant C: Panel expansion state ──
  // Now defaults to expanded (the surface the user looks at first).
  // Outlook defaults to collapsed; in v1 it always shows a Pending callout
  // when expanded, so leaving it collapsed by default reduces visual noise.
  const [nowExpanded, setNowExpanded] = useState<boolean>(true);
  const [outlookExpanded, setOutlookExpanded] = useState<boolean>(false);

  // ── Pull component-level breakdowns from the engine output ──
  // For Now: completeness + timeliness components. For Outlook: the four
  // leading signals (entry_latency_trend, engagement, drift_risk,
  // preflight_risk) — all applicable=false in v1.
  const nowComponentByKey = useMemo(() => {
    const map = new Map<string, ComponentResult>();
    for (const c of now?.components ?? []) map.set(c.key, c);
    return map;
  }, [now]);
  const outlookComponentByKey = useMemo(() => {
    const map = new Map<string, ComponentResult>();
    for (const c of outlook?.components ?? []) map.set(c.key, c);
    return map;
  }, [outlook]);

  // ── Render ──

  // Empty state: no metrics configured at all
  if (totalCount === 0 && (!now || !now.applicable)) {
    return (
      <div style={{ padding: "0 4px" }}>
        <div className={styles.emptyState}>
          {t("dataReliability.emptyState") || "No metrics configured. Add KPIs in Project Settings → KPI Configuration to track data reliability."}
        </div>
      </div>
    );
  }

  // ── Now panel rendering helpers ──
  const nowScore = now?.score ?? null;
  const nowColor = now?.color ?? 0;
  const nowTrend: -1 | 0 | 1 = now?.trend ?? 0;
  const nowApplicable = !!now?.applicable;
  const completenessComp = nowComponentByKey.get("completeness");
  const timelinessComp = nowComponentByKey.get("timeliness");

  // Outlook panel rendering helpers (always N/A in v1 by stub-input design)
  const outlookScore = outlook?.score ?? null;
  const outlookColor = outlook?.color ?? 0;
  const outlookApplicable = !!outlook?.applicable;

  // Status pills rely on the matrix's MetricRowStatus values; the i18n keys
  // for them are localized below. Keep them in one map for cleanliness.

  return (
    <div style={{ padding: "0 4px" }}>
      {/* ── Completed-project banner (DEV-851 blue Complete state) ── */}
      {isCompletedProject && (
        <div className={styles.completedBanner}>
          <span className={styles.completedBannerIcon} aria-hidden="true">✓</span>
          <div>
            <div className={styles.completedBannerLabel}>
              {t("dataReliability.completeStateBannerLabel") || "Project Complete"}
            </div>
            <div className={styles.completedBannerSubtitle}>
              {t("dataReliability.completeStateBannerSubtitle") || "Reliability scoring is paused for completed projects. Matrix below shows historical reporting state."}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Now panel (lagging signal — Completeness + Timeliness) ═══ */}
      <div
        className={styles.compositePanel}
        data-color={nowColor}
        data-completed={isCompletedProject ? "true" : "false"}
        data-expanded={nowExpanded ? "true" : "false"}
      >
        <button
          type="button"
          className={`${styles.panelHeader} ${styles.scoreBanner}`}
          onClick={() => setNowExpanded((v) => !v)}
          aria-expanded={nowExpanded}
          aria-label={
            nowExpanded
              ? (t("dataReliability.collapsePanel") || "Collapse panel")
              : (t("dataReliability.expandPanel") || "Expand panel")
          }
        >
          {/*
            DEV-972. Score-circle renders the actual engine score (or '—'
            when null) regardless of completion state. data-color still
            switches to the "completed" palette code (4 = blue) for
            completed projects so the indicator turns blue — that matches
            DEV-966's rule that blue is the only completion signal across
            the app. Previously the score was swapped for a '✓' on
            completed projects, which hid the actual reliability number
            and was visually inconsistent with the rest of the KPI
            surfaces. The explanatory banner above the panel still tells
            the user that "scoring is paused" so the blue + (sometimes)
            '—' combination remains legible.
          */}
          <div
            className={styles.scoreCircle}
            data-color={isCompletedProject ? 4 : nowColor}
            aria-hidden="true"
          >
            {nowApplicable && nowScore !== null ? Math.round(nowScore) : "—"}
            {nowApplicable && nowScore !== null && (
              <span className={styles.scoreDenom}>/100</span>
            )}
          </div>

          <div className={styles.scoreMeta}>
            <div className={styles.panelEyebrow}>
              {t("dataReliability.nowEyebrow") || "Now"}
            </div>
            <div className={styles.scoreTitle}>
              {t("dataReliability.title") || "Data Reliability"}
              <span
                className={styles.summaryFeedTag}
                title={t("dataReliability.summaryFeedTag") || "Feeds Summary KPI"}
              >
                {t("dataReliability.summaryFeedTag") || "Feeds Summary KPI"}
              </span>
            </div>
            <div className={styles.scoreSubtitle}>
              {asOfDate ? `${t("dataReliability.asOf") || "As of"} ${formatDateDdMmm(asOfDate)}` : ""}
              {!nowApplicable && now?.reason && (
                <span style={{ marginInlineStart: 8, fontStyle: "italic" }}>
                  · {now.reason}
                </span>
              )}
            </div>
          </div>

          <div className={styles.scoreBreakdown}>
            {/* Order matches the matrix column order below: timeliness
                on the left (mirrors the "Avg. Lag" column position),
                completeness on the right (mirrors "Comp." being last). */}
            <div className={styles.breakdownMetric}>
              <span
                className={styles.breakdownValue}
                data-color={
                  timelinessComp?.applicable && timelinessComp.score !== null
                    ? colorForScore(timelinessComp.score)
                    : 0
                }
              >
                {timelinessComp?.applicable && timelinessComp.score !== null
                  ? `${timelinessComp.score}%`
                  : (t("dataReliability.notApplicableShort") || "N/A")
                }
              </span>
              <span className={styles.breakdownLabel}>
                {t("dataReliability.timeliness") || "Timeliness"}
              </span>
            </div>
            <div className={styles.breakdownMetric}>
              <span
                className={styles.breakdownValue}
                data-color={
                  completenessComp?.applicable && completenessComp.score !== null
                    ? colorForScore(completenessComp.score)
                    : 0
                }
              >
                {completenessComp?.applicable && completenessComp.score !== null
                  ? `${completenessComp.score}%`
                  : (t("dataReliability.notApplicableShort") || "N/A")
                }
              </span>
              <span className={styles.breakdownLabel}>
                {t("dataReliability.completeness") || "Completeness"}
              </span>
            </div>
          </div>

          <span className={styles.panelTrend} data-trend={nowTrend} aria-hidden="true">
            {trendSymbol(nowTrend)}
          </span>
          <span className={styles.panelChevron} aria-hidden="true">▼</span>
        </button>

        <div className={styles.panelBody}>
          {/* Matrix counter line (preserved from original layout) */}
          <div className={styles.completenessHeader}>
            <span className={styles.completenessCount}>{reportedCount}</span>
            <span className={styles.completenessOf}>{t("dataReliability.of") || "of"}</span>
            <span className={styles.completenessCount}>{totalCount}</span>
            <span className={styles.completenessDesc}>
              {t("dataReliability.metricsReported") || "metrics reported this period"}
            </span>
          </div>

          {displayGroups.length > 0 && (
            <table className={styles.matrixTable}>
              <thead>
                <tr>
                  <th style={{ width: "5%" }}></th>
                  <th>{t("dataReliability.colMetric") || "Metric"}</th>
                  <th className={styles.colCadence}>{t("dataReliability.colCadence") || "Cadence"}</th>
                  <th className={styles.colLastReported}>{t("dataReliability.colLastReported") || "Last reported"}</th>
                  <th className={styles.colMissing} style={{ textAlign: "right" }}>
                    {t("dataReliability.colMissing") || "Missing"}
                  </th>
                  <th className={styles.colTime} style={{ textAlign: "right" }}>
                    {t("dataReliability.colTime") || "Avg. Lag"}
                  </th>
                  <th>{t("dataReliability.colComp") || "Comp."}</th>
                  {/* Task #65: Due By column dropped — Missing replaces its
                      diagnostic value (it shows the count of unreported
                      cadence periods directly rather than asking the user
                      to compute it from the next-due date). The cadence-
                      curve logic that computed dueBy is still useful for
                      other surfaces (Action Center deadlines) so it stays
                      in the row builder, just not rendered here.
                      Task #75: "Status" header renamed to "Comp." (it shows
                      completeness-driven state); new "Time" column shows
                      avg submission lag per instance. */}
                </tr>
              </thead>
              <tbody>
                {displayGroups.map((group) => (
                  <React.Fragment key={group.domain}>
                    {/* Group header row */}
                    <tr className={styles.groupHeaderRow}>
                      <td colSpan={6} className={styles.groupHeaderCell}>
                        <span className={styles.groupName}>
                          {t(group.labelKey) || group.domain}
                        </span>
                        <span className={styles.groupWeight}>
                          {group.weight}%
                        </span>
                      </td>
                      <td className={styles.groupHeaderCell}>
                        <span className={styles.groupCount}>
                          {group.reportedCount}/{group.totalCount}
                        </span>
                      </td>
                    </tr>
                    {/* Metric rows within group */}
                    {group.rows.map((row) => {
                      // Task #65 — when reliabilityState is set the row
                      // uses the new pill (icon + word); otherwise it
                      // falls back to the legacy status text.
                      const v2 = row.reliabilityState;
                      const v2Label =
                        v2 === "current" ? (t("dataReliability.statusCurrent") || "Current")
                        : v2 === "backlogged" ? (t("dataReliability.statusBacklogged") || "Backlogged")
                        : v2 === "late" ? (t("dataReliability.statusLateV2") || "Late")
                        : v2 === "falling-behind" ? (t("dataReliability.statusFallingBehind") || "Falling behind")
                        : v2 === "never-reported" ? (t("dataReliability.statusNeverReported") || "Never reported")
                        : v2 === "not-yet-due" ? (t("dataReliability.statusNotYetDue") || "Not yet due")
                        : "";
                      const v2Icon =
                        v2 === "current" ? "ti-circle-check"
                        : v2 === "backlogged" ? "ti-history"
                        : v2 === "late" ? "ti-clock-exclamation"
                        : v2 === "falling-behind" ? "ti-trending-down"
                        : v2 === "never-reported" ? "ti-circle-x"
                        : v2 === "not-yet-due" ? "ti-clock"
                        : "";
                      const missingDisplay =
                        row.missing == null || row.expected == null
                          ? "—"
                          : row.expected === 0
                            ? "—"
                            : `${row.missing} / ${row.expected}`;
                      const missingNeverReported =
                        v2 === "never-reported"
                          ? (t("dataReliability.lastReportedNever") || "Never reported")
                          : null;

                      return (
                        <tr key={row.key}>
                          <td>
                            <span className={styles.colorBar} data-color={row.colorValue} />
                          </td>
                          <td>
                            <span className={styles.metricName}>{row.label}</span>
                          </td>
                          <td className={styles.colCadence}>
                            <span className={styles.cadenceBadge}>
                              {CADENCE_LABEL_KEYS[row.cadence]
                                ? (t(CADENCE_LABEL_KEYS[row.cadence]) || row.cadence)
                                : row.cadence}
                            </span>
                          </td>
                          <td className={`${styles.dateCell} ${styles.colLastReported}`}>
                            {missingNeverReported ?? formatDateDdMmm(row.lastReported)}
                          </td>
                          <td
                            className={styles.colMissing}
                            data-state={v2 ?? ""}
                            style={{ textAlign: "right" }}
                            title={
                              row.missing != null && row.expected != null
                                ? `${row.missing} missing of ${row.expected} expected periods`
                                : undefined
                            }
                          >
                            {missingDisplay}
                          </td>
                          {(() => {
                            // Avg. Lag column. Avg days late per instance
                            // with color bands relative to grace:
                            //   ≤ grace                — green (on time / within tolerance)
                            //   grace+1 to grace+7     — yellow (drifting)
                            //   > grace+7              — red (chronically late)
                            const lag = row.avgLagDays;
                            const grace = Number.isFinite(row.graceDays)
                              ? (row.graceDays as number)
                              : 0;
                            let timeBand: "ok" | "warn" | "bad" | "na" = "na";
                            let timeLabel = "—";
                            if (lag != null && Number.isFinite(lag)) {
                              timeLabel = `${lag.toFixed(1)}d`;
                              if (lag <= grace) timeBand = "ok";
                              else if (lag <= grace + 7) timeBand = "warn";
                              else timeBand = "bad";
                            }
                            return (
                              <td
                                className={styles.colTime}
                                data-band={timeBand}
                                style={{ textAlign: "right" }}
                                title={
                                  lag != null && Number.isFinite(lag)
                                    ? `Average ${lag.toFixed(2)} days late across submissions (grace ${grace}d)`
                                    : undefined
                                }
                              >
                                {timeLabel}
                              </td>
                            );
                          })()}
                          <td>
                            {v2 ? (
                              <span
                                className={styles.statusPill}
                                data-state={v2}
                                title={
                                  row.missing != null && row.expected != null
                                    ? `${v2Label} · ${row.missing} / ${row.expected} missing`
                                    : v2Label
                                }
                                aria-label={v2Label}
                              >
                                <i
                                  className={`ti ${v2Icon} ${styles.statusPillIcon}`}
                                  aria-hidden="true"
                                />
                                <span className={styles.statusPillLabel}>{v2Label}</span>
                              </span>
                            ) : (
                              <span className={styles.statusBadge} data-status={row.status}>
                                {row.status === "on-time" && `✓ ${t("dataReliability.statusOnTime") || "On time"}`}
                                {row.status === "reported" && `● ${t("dataReliability.statusReported") || "Reported"}`}
                                {row.status === "late" && `⚠ ${row.lateDays ?? ""}${row.lateDays ? `d ${t("dataReliability.statusLate") || "late"}` : t("dataReliability.statusLate") || "Late"}`}
                                {row.status === "missing" && `✗ ${t("dataReliability.statusMissing") || "Missing"}`}
                                {row.status === "not-due" && `◦ ${t("dataReliability.statusNotDue") || "Not yet due"}`}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ═══ Outlook panel (leading signal — always N/A in v1) ═══ */}
      {/*
        DEV-972. data-color goes to the "completed" palette code (4 = blue)
        for completed projects, matching the Now panel above and the
        DEV-966 rule that blue is the only completion signal app-wide.
        The score itself is preserved (Outlook never swapped to a checkmark)
        so the natural engine value still shows.
      */}
      <div
        className={styles.compositePanel}
        data-color={isCompletedProject ? 4 : outlookColor}
        data-expanded={outlookExpanded ? "true" : "false"}
      >
        <button
          type="button"
          className={`${styles.panelHeader} ${styles.scoreBanner}`}
          onClick={() => setOutlookExpanded((v) => !v)}
          aria-expanded={outlookExpanded}
          aria-label={
            outlookExpanded
              ? (t("dataReliability.collapsePanel") || "Collapse panel")
              : (t("dataReliability.expandPanel") || "Expand panel")
          }
        >
          <div
            className={styles.scoreCircle}
            data-color={isCompletedProject ? 4 : outlookColor}
            aria-hidden="true"
          >
            {outlookApplicable && outlookScore !== null ? Math.round(outlookScore) : "—"}
            {outlookApplicable && outlookScore !== null && (
              <span className={styles.scoreDenom}>/100</span>
            )}
          </div>

          <div className={styles.scoreMeta}>
            <div className={styles.panelEyebrow}>
              {t("dataReliability.outlookEyebrow") || "Outlook"}
            </div>
            <div className={styles.scoreTitle}>
              {t("dataReliability.outlookSubtitle") || "Leading — what may happen next"}
            </div>
            <div className={styles.scoreSubtitle}>
              {!outlookApplicable && (outlook?.reason || t("dataReliability.statusPending") || "Pending")}
            </div>
          </div>

          <div className={styles.scoreBreakdown}>
            {[
              { key: "entry_latency_trend", labelKey: "dataReliability.signalEntryLatency", fallback: "Latency" },
              { key: "engagement",          labelKey: "dataReliability.signalEngagement",   fallback: "Engagement" },
              { key: "drift_risk",          labelKey: "dataReliability.signalDriftRisk",    fallback: "Drift" },
              { key: "preflight_risk",      labelKey: "dataReliability.signalPreflightRisk", fallback: "Pre-flight" },
            ].map(({ key, labelKey, fallback }) => {
              const c = outlookComponentByKey.get(key);
              const applicable = !!c?.applicable;
              const score = c?.score ?? null;
              return (
                <div className={styles.breakdownMetric} key={key}>
                  <span
                    className={styles.breakdownValue}
                    data-color={applicable && score !== null ? colorForScore(score) : 0}
                  >
                    {applicable && score !== null
                      ? `${score}`
                      : (t("dataReliability.notApplicableShort") || "N/A")
                    }
                  </span>
                  <span className={styles.breakdownLabel}>
                    {t(labelKey) || fallback}
                  </span>
                </div>
              );
            })}
          </div>

          <span className={styles.panelChevron} aria-hidden="true">▼</span>
        </button>

        <div className={styles.panelBody}>
          {!outlookApplicable && (
            <div className={styles.outlookPendingCallout}>
              {t("dataReliability.outlookPendingExplain")
                || "Outlook combines four leading signals — entry-latency trend, engagement, drift risk, and pre-flight risk — to forecast where reliability is headed. Data feeds activate in a follow-up release."}
            </div>
          )}

          <table className={styles.outlookSignalTable}>
            <thead>
              <tr>
                <th>{t("dataReliability.colMetric") || "Signal"}</th>
                <th>{t("dataReliability.colStatus") || "Status"}</th>
              </tr>
            </thead>
            <tbody>
              {[
                { key: "entry_latency_trend", labelKey: "dataReliability.signalEntryLatency", fallback: "Entry latency trend" },
                { key: "engagement",          labelKey: "dataReliability.signalEngagement",   fallback: "Engagement" },
                { key: "drift_risk",          labelKey: "dataReliability.signalDriftRisk",    fallback: "Drift risk" },
                { key: "preflight_risk",      labelKey: "dataReliability.signalPreflightRisk", fallback: "Pre-flight risk" },
              ].map(({ key, labelKey, fallback }) => {
                const c = outlookComponentByKey.get(key);
                const applicable = !!c?.applicable;
                return (
                  <tr key={key}>
                    <td>
                      <span className={styles.outlookSignalName}>
                        {t(labelKey) || fallback}
                      </span>
                    </td>
                    <td>
                      {applicable ? (
                        <span className={styles.statusBadge} data-status="on-time">
                          {c?.score !== null ? `${c?.score}` : "—"}
                        </span>
                      ) : (
                        <span className={styles.statusBadge} data-status="reported">
                          {t("dataReliability.statusPending") || "Pending"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
